import {
  AdditionalMaterials,
  Header,
  HeaderOffset,
  HeroHeaderBelowContainer,
  HeroHeaderContainer,
  Preparation,
  ProductDescription,
  ProductImages,
  ProductMain,
  ProductMeta,
  ProductProvider,
  ProductSeo,
  RelatedProducts,
  StickyAddToCart,
  Suspects,
  useMediaQuery,
  useOnScreen,
} from "@cm-websites/common";
import { Box, Container, Grid, Paper, useTheme } from "@mui/material";
import { Footer } from "../../footer";
import { getProductStaticPaths, getProductStaticProps } from "@cm-websites/common/dist/cms-api";
import { HEADER_ITEMS } from "../../header";
import i18nConfig from "../../../next-i18next.config";
import React from "react";
import type { Product } from "@cm-websites/common";

type ProductPageProps = { product: Product; storeId: number | string };

export const ProductPage: React.FC<ProductPageProps> = ({ product, storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isAtleastMd = useMediaQuery(theme.breakpoints.up("md"));
  const addToCartBtnRef = React.useRef<HTMLDivElement>();
  const [isIntersecting, isAboveViewport] = useOnScreen(addToCartBtnRef, { threshold: 0.4 });
  const showStickyAddToCart = isMobile && !isIntersecting && isAboveViewport;

  return (
    <ProductProvider edition="online" product={product} storeId={storeId}>
      <ProductSeo domain="culinario-mortale.de" />
      <HeroHeaderContainer backgroundColor={product.colors.accent1} id="product-hero-header">
        <Header items={HEADER_ITEMS} />
        <HeaderOffset />
        {isAtleastMd && (
          <Container
            id="desktop-product-main-container"
            maxWidth={isMd ? "lg" : "xl"}
            sx={{ pt: 3, pb: 6 }}
          >
            <Grid
              alignItems={{ md: "center", lg: "center", xl: "stretch" }}
              container
              spacing={{ md: 2, lg: 5, xl: 10 }}
            >
              <Grid
                id="desktop-product-main-images"
                item
                sx={{ marginTop: { xs: "-1rem", sm: "-2rem", md: 0 } }}
                md={6}
                lg={7}
                xl={7}
                xs={12}
              >
                <ProductImages mode={isTablet ? "horizontal" : "vertical"} />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                lg={5}
                xl={5}
                id="desktop-product-main-overview"
                sx={{ display: "flex" }}
              >
                <Paper
                  sx={{
                    background: "white",
                    borderBottomLeftRadius: { xs: 0, md: "16px" },
                    borderBottomRightRadius: { xs: 0, md: "16px" },
                    borderTopLeftRadius: { xs: "16px", sm: "16px" },
                    borderTopRightRadius: { xs: "16px", sm: "16px" },
                    boxShadow: `rgba(17, 12, 46, 0.15) 0px 48px 100px 0px`,
                    maxWidth: "100%",
                    pb: { md: 3, lg: 4 },
                    pt: { md: 4, lg: 5 },
                    px: { md: 3, lg: 4, xl: 5 },
                  }}
                >
                  <ProductMain />
                </Paper>
              </Grid>
            </Grid>
          </Container>
        )}
        {!isAtleastMd && (
          <Box component="section" id="product-images" sx={{ backgroundColor: product.colors.accent1 }}>
            <ProductImages mode={isTablet ? "horizontal" : "vertical"} />
          </Box>
        )}
      </HeroHeaderContainer>

      <HeroHeaderBelowContainer>
        {!isAtleastMd && (
          <Paper
            component="section"
            className="desktop-add-to-cart"
            sx={{
              background: "white",
              borderTop: `none`,
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              maxWidth: "100%",
              py: { xs: 4, sm: 5 },
              boxShadow: `
                0px -2.2px 2.2px -15px rgba(0, 0, 0, 0.02),
                0px -5.3px 5.3px -15px rgba(0, 0, 0, 0.028),
                0px -10px 10px -15px rgba(0, 0, 0, 0.035),
                0px -17.9px 17.9px -15px rgba(0, 0, 0, 0.042),
                0px -33.4px 33.4px -15px rgba(0, 0, 0, 0.05),
                0px -80px 80px -15px rgba(0, 0, 0, 0.07)
              `,
            }}
          >
            <Container maxWidth="xl">
              <ProductMain addToCartBtnRef={addToCartBtnRef} />
            </Container>
          </Paper>
        )}

        <ProductDescription />
        <ProductMeta />
        <Suspects />
        <AdditionalMaterials />
        <Preparation />
        <RelatedProducts />
        <Footer />
      </HeroHeaderBelowContainer>

      <StickyAddToCart show={showStickyAddToCart || false} />
    </ProductProvider>
  );
};

export default ProductPage;

export const getStaticProps = getProductStaticProps({
  edition: "online",
  fields: [
    "*.*",
    "relatedCases.related_Culinario_Mortale_id.*.*",
    "printVersionImages.*",
    "printVersionImages.directus_files_id.mask",
    "printVersionImages.directus_files_id.id",
    "printVersionImages.directus_files_id.description",
    "onlineVersionImages.*",
    "onlineVersionImages.directus_files_id.mask",
    "onlineVersionImages.directus_files_id.id",
    "onlineVersionImages.directus_files_id.description",
  ],
  i18nConfig,
  origin: "ONLINE SLUG",
});

export const getStaticPaths = getProductStaticPaths({
  language: "DE",
  status: "published",
  hasOnlineVersion: true,
});
